import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const RowStat = ({ label, value, tone = "default" }) => {
  const colors = {
    default: "bg-slate-50 text-slate-700",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    bad: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${colors[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
};

export default function DisasterRecovery() {
  const [uploads, setUploads] = useState([]);
  const [selected, setSelected] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [corrections, setCorrections] = useState(null);
  const [recompute, setRecompute] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/attendance/uploads");
        setUploads(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error("Failed to load uploads list");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runDryRun = async () => {
    if (!selected) {
      toast.error("Select an upload first");
      return;
    }
    setDryRun({ loading: true });
    try {
      const { data } = await api.post(
        `/attendance/uploads/${selected}/replay-audit-overrides?dry_run=true`
      );
      setDryRun(data);
      toast.success("Dry run complete — review numbers below");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Dry run failed: ${detail}`);
      setDryRun(null);
    }
  };

  const commitReplay = async () => {
    if (!selected) return;
    if (!window.confirm(
      "COMMIT the replay? This will overwrite values in the freshly re-uploaded attendance. " +
      "Make sure you have run a dry run first."
    )) return;
    setCommitting(true);
    try {
      const { data } = await api.post(
        `/attendance/uploads/${selected}/replay-audit-overrides?dry_run=false`
      );
      setDryRun(data);
      toast.success(
        `Replay complete: per-row=${data?.per_row?.replayed}, ` +
        `mark-day rows=${data?.mark_day?.replayed_rows}`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Replay failed: ${detail}`);
    } finally {
      setCommitting(false);
    }
  };

  const runRecomputeDryRun = async () => {
    if (!selected) { toast.error("Select an upload first"); return; }
    setRecompute({ loading: true });
    try {
      const { data } = await api.post(
        `/attendance/uploads/${selected}/recompute-grades?dry_run=true`
      );
      setRecompute(data);
      toast.success(`Dry run: ${data.changed} rows would be updated`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Dry run failed: ${detail}`);
      setRecompute(null);
    }
  };

  const commitRecompute = async () => {
    if (!selected || !recompute) return;
    if (!window.confirm(
      `COMMIT recompute? ${recompute.changed} rows will be updated. ` +
      `Upload status will revert to 'review' and monthly_summary will be cleared. ` +
      `You will need to run Finalize again afterwards.`
    )) return;
    setRecomputing(true);
    try {
      const { data } = await api.post(
        `/attendance/uploads/${selected}/recompute-grades?dry_run=false`
      );
      setRecompute(data);
      toast.success(`Recomputed ${data.changed} rows. Run Finalize next.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Recompute failed: ${detail}`);
    } finally {
      setRecomputing(false);
    }
  };

  const loadCorrections = async () => {
    if (!selected) return;
    setCorrections({ loading: true });
    try {
      const { data } = await api.get(
        `/attendance/uploads/${selected}/audit-corrections`
      );
      setCorrections(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Failed to load corrections: ${detail}`);
      setCorrections(null);
    }
  };

  const downloadCsv = () => {
    if (!corrections?.entries) return;
    const cols = [
      "at", "user_email", "kind", "employee_id", "date",
      "first_in_before", "first_in_after",
      "last_out_before", "last_out_after",
      "grade_before", "grade_after",
      "ot_before", "ot_after",
      "is_holiday", "is_off", "day", "mode", "reason", "record_id",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replaceAll('"', '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [cols.join(",")].concat(
      corrections.entries.map((e) => cols.map((c) => esc(e[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-corrections-${corrections.month || "month"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedUpload = uploads.find((u) => u.id === selected);

  return (
    <div data-testid="disaster-recovery-page" className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Settings</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Disaster Recovery — Attendance Replay
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          After a hard-delete + CSV re-upload, this tool replays manual time
          corrections, anomaly fixes, and mark-day overrides from the audit
          log onto the fresh upload. Always run <b>Dry Run</b> first.
        </p>
      </div>

      {/* Step 1 — pick upload */}
      <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">1</div>
          <h2 className="font-semibold text-slate-900">Select the re-uploaded month</h2>
        </div>
        <p className="text-xs text-slate-500">
          Pick the upload row you just re-created from the raw biometric CSV.
        </p>
        <select
          data-testid="upload-selector"
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          value={selected}
          onChange={(e) => { setSelected(e.target.value); setDryRun(null); setCorrections(null); }}
          disabled={loading}
        >
          <option value="">— Choose an upload —</option>
          {uploads.map((u) => (
            <option key={u.id} value={u.id}>
              {u.month} • {u.unit_name} • {u.filename} • uploaded {formatDateTime(u.uploaded_at)} • {u.id.slice(0, 8)}
            </option>
          ))}
        </select>
      </section>

      {/* Step 2 — replay */}
      <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">2</div>
          <h2 className="font-semibold text-slate-900">Replay audit overrides</h2>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="dry-run-btn"
            onClick={runDryRun}
            disabled={!selected || dryRun?.loading}
            variant="outline"
          >
            {dryRun?.loading ? "Scanning..." : "Run Dry Run"}
          </Button>
          <Button
            data-testid="commit-btn"
            onClick={commitReplay}
            disabled={!selected || committing || !dryRun || dryRun?.loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {committing ? "Committing..." : "Commit Replay"}
          </Button>
        </div>

        {dryRun && !dryRun.loading && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Mode: <Badge variant="outline" className={dryRun.dry_run ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}>
                {dryRun.dry_run ? "DRY RUN — nothing written" : "COMMITTED"}
              </Badge>
              {" · "}Month: <b>{dryRun.month}</b>
              {" · "}Total audit entries scanned: <b>{dryRun.audits_scanned}</b>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <RowStat label="Per-row replayed" value={dryRun.per_row?.replayed ?? 0} tone="good" />
              <RowStat label="Missing in fresh upload" value={dryRun.per_row?.missing_in_fresh_upload ?? 0} tone="warn" />
              <RowStat label="Legacy (no emp/date)" value={dryRun.per_row?.legacy_unreplayable_no_emp_date ?? 0} tone="warn" />
              <RowStat label="Mark-day rows" value={dryRun.mark_day?.replayed_rows ?? 0} tone="good" />
            </div>
            {dryRun.per_row?.sample?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 font-medium">Sample per-row edits ({dryRun.per_row.sample.length})</summary>
                <pre className="mt-2 bg-slate-50 p-3 rounded text-[11px] overflow-x-auto">{JSON.stringify(dryRun.per_row.sample, null, 2)}</pre>
              </details>
            )}
            {dryRun.mark_day?.sample?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 font-medium">Mark-day operations ({dryRun.mark_day.sample.length})</summary>
                <pre className="mt-2 bg-slate-50 p-3 rounded text-[11px] overflow-x-auto">{JSON.stringify(dryRun.mark_day.sample, null, 2)}</pre>
              </details>
            )}
            {dryRun.note && (
              <div className="text-xs text-slate-500 italic">{dryRun.note}</div>
            )}
          </div>
        )}
      </section>

      {/* Step 2.5 — Recompute grades from raw punches (parser fix) */}
      <section className="bg-amber-50 border border-amber-200 rounded-md p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-amber-200 text-amber-800 text-xs font-bold flex items-center justify-center">2½</div>
          <h2 className="font-semibold text-slate-900">Recompute grades from raw punches</h2>
        </div>
        <p className="text-xs text-slate-700 max-w-3xl">
          Use this if punch data looks correct in the grid but grades are
          stuck on "A" or OT is "—". Re-parses <code>first_in</code>/
          <code>last_out</code> using the latest rules and recalculates
          worked-minutes, grade, OT for every non-overridden row. After
          commit, run <b>Finalize → Monthly</b> again.
        </p>
        <div className="flex gap-2">
          <Button
            data-testid="recompute-dry-run-btn"
            onClick={runRecomputeDryRun}
            disabled={!selected || recompute?.loading}
            variant="outline"
          >
            {recompute?.loading ? "Scanning..." : "Dry-Run Recompute"}
          </Button>
          <Button
            data-testid="recompute-commit-btn"
            onClick={commitRecompute}
            disabled={!selected || recomputing || !recompute || recompute?.loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {recomputing ? "Committing..." : "Commit Recompute"}
          </Button>
        </div>
        {recompute && !recompute.loading && (
          <div className="space-y-3">
            <div className="text-xs text-slate-600">
              Mode: <Badge variant="outline" className={recompute.dry_run ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}>
                {recompute.dry_run ? "DRY RUN" : "COMMITTED"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <RowStat label="Total rows" value={recompute.total_rows ?? 0} />
              <RowStat label="Will change" value={recompute.changed ?? 0} tone="good" />
              <RowStat label="No change" value={recompute.no_change ?? 0} />
              <RowStat label="Overrides preserved" value={recompute.preserved_overrides ?? 0} tone="warn" />
            </div>
            {recompute.sample?.length > 0 && (
              <details className="text-xs" open>
                <summary className="cursor-pointer text-slate-700 font-medium">Sample changes ({recompute.sample.length})</summary>
                <pre className="mt-2 bg-white border border-amber-200 p-3 rounded text-[11px] overflow-x-auto">{JSON.stringify(recompute.sample, null, 2)}</pre>
              </details>
            )}
            {recompute.note && (
              <div className="text-xs text-amber-800 italic">{recompute.note}</div>
            )}
          </div>
        )}
      </section>

      {/* Step 3 — review unreplayable manually */}
      <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">3</div>
          <h2 className="font-semibold text-slate-900">Review legacy corrections for manual re-entry</h2>
        </div>
        <p className="text-xs text-slate-500 max-w-2xl">
          Older audit entries (before audit-enrichment) lack employee_id+date and
          cannot be auto-replayed. Download the CSV, look up the employee by
          record_id in your old reports, and re-apply via the normal HR Review
          screen.
        </p>
        <div className="flex gap-2">
          <Button
            data-testid="load-corrections-btn"
            onClick={loadCorrections}
            disabled={!selected || corrections?.loading}
            variant="outline"
          >
            {corrections?.loading ? "Loading..." : "Load Corrections List"}
          </Button>
          {corrections?.entries && (
            <Button
              data-testid="download-csv-btn"
              onClick={downloadCsv}
              variant="outline"
            >
              Download CSV ({corrections.total})
            </Button>
          )}
        </div>
        {corrections?.entries && (
          <div className="border border-slate-200 rounded-md overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">When</th>
                  <th className="px-2 py-2 text-left">By</th>
                  <th className="px-2 py-2 text-left">Kind</th>
                  <th className="px-2 py-2 text-left">Emp/Day</th>
                  <th className="px-2 py-2 text-left">Grade</th>
                  <th className="px-2 py-2 text-left">OT</th>
                  <th className="px-2 py-2 text-left">In→Out</th>
                  <th className="px-2 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {corrections.entries.slice(0, 200).map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="px-2 py-1 text-slate-500 font-mono">{formatDateTime(e.at)}</td>
                    <td className="px-2 py-1 text-slate-600">{e.user_email}</td>
                    <td className="px-2 py-1">
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {e.kind || "—"}
                      </Badge>
                    </td>
                    <td className="px-2 py-1 font-mono text-slate-700">
                      {e.employee_id ? e.employee_id.slice(0, 8) : "—"}
                      {e.date ? ` · ${e.date}` : (e.day ? ` · day ${e.day}` : "")}
                    </td>
                    <td className="px-2 py-1">
                      {(e.grade_before ?? "—")} → <b>{e.grade_after ?? "—"}</b>
                    </td>
                    <td className="px-2 py-1 tabular-nums">
                      {(e.ot_before ?? "—")} → <b>{e.ot_after ?? "—"}</b>
                    </td>
                    <td className="px-2 py-1 font-mono">
                      {e.first_in_after || e.last_out_after ? `${e.first_in_after || "—"} / ${e.last_out_after || "—"}` : "—"}
                    </td>
                    <td className="px-2 py-1 text-slate-600 max-w-xs truncate">{e.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {corrections.entries.length > 200 && (
              <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t">
                Showing first 200 of {corrections.total}. Download CSV for full list.
              </div>
            )}
          </div>
        )}
      </section>

      {selectedUpload && (
        <div className="text-[11px] text-slate-400 font-mono">
          Selected upload: {selectedUpload.id}
        </div>
      )}
    </div>
  );
}
